import { LocalDate } from '@js-joda/core';
import { uniqueId } from 'lodash';
import { useCallback, useMemo } from 'react';

import { Guardian, GuardianQuery } from '@dreryk/egabinet-patient-frontend-api';

import { Slot } from '@dreryk/watermelon';
import { paginations } from '@dreryk/command-query-ws-client';
import { UseCqrsQueryResult, useCqrsSearch } from '@dreryk/command-query-react-common';

import { MedicalLeaveFormAddress } from '../../../common/common.types';
import { GuardianMedicalLeaveFormController, GuardianMedicalLeaveFormGuardian } from '../../guardianMedicalLeave.types';

import { mapGuardianPersonalDataToFormGuardian } from './GuardianSelect.utils';

const pagination = paginations.all<Guardian>();

export const useMedicalLeaveGuardiansQuery = (patientId: number): UseCqrsQueryResult<Guardian[]> => {
   return useCqrsSearch(() => {
      const guardiansQuery = new GuardianQuery();
      guardiansQuery.patientId = Slot.of(patientId);
      guardiansQuery.identifierTypes = Slot.of(['PESEL', 'PASSPORT']);
      return guardiansQuery;
   }, pagination);
};

type GuardianSelectItem = { id: string | number } & GuardianMedicalLeaveFormGuardian;

export const useGuardianSelectItems = (
   guardians: Guardian[],
   initialGuardian?: GuardianMedicalLeaveFormGuardian,
   manuallyAddedGuardians?: GuardianMedicalLeaveFormGuardian[]
): GuardianSelectItem[] => {
   return useMemo(() => {
      const selectGuardians: GuardianSelectItem[] = guardians.map((guardian) => ({
         ...mapGuardianPersonalDataToFormGuardian(guardian),
         id: guardian.id,
      }));
      if (initialGuardian) {
         selectGuardians.unshift({ ...initialGuardian, id: uniqueId('guardian') });
      }
      if (manuallyAddedGuardians) {
         manuallyAddedGuardians.forEach((addedGuardian) => {
            selectGuardians.unshift({
               id: uniqueId('guardian'),
               ...addedGuardian,
               address: addedGuardian.address,
            });
         });
      }
      return selectGuardians;
   }, [guardians, initialGuardian, manuallyAddedGuardians]);
};

export const useSetAddress = (
   controller: GuardianMedicalLeaveFormController
): ((address?: MedicalLeaveFormAddress) => void) =>
   useCallback(
      (address) => {
         controller.setValue(
            'address',
            {
               city: address?.city ?? null,
               flat: address?.flat ?? null,
               house: address?.house ?? null,
               street: address?.street ?? null,
               postcode: address?.postcode ?? null,
               country: address?.country ?? null,
            },
            { shouldValidate: true }
         );
         controller.setValue('address.postcode', address?.postcode ?? null, { shouldValidate: true });
         controller.setValue('address.country', address?.country ?? null, { shouldValidate: true });
      },
      [controller]
   );

export const useSetBirthDate = (controller: GuardianMedicalLeaveFormController): ((birthDate?: LocalDate) => void) =>
   useCallback(
      (birthDate) => {
         controller.setValue('birthDate', birthDate ?? null);
      },
      [controller]
   );
